/// <reference types="vite/client" />
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React, { type ComponentType, type ReactNode } from 'react';

const apiKey = import.meta.env.VITE_BUGSNAG_API_KEY;

type ErrorBoundaryProps = {
  children?: ReactNode;
};

/** Pass-through boundary used when Bugsnag is not configured (e.g. local/CI builds). */
const PassthroughErrorBoundary: ComponentType<ErrorBoundaryProps> = ({ children }) =>
  React.createElement(React.Fragment, null, children);

let ErrorBoundary: ComponentType<ErrorBoundaryProps> = PassthroughErrorBoundary;

if (apiKey) {
  Bugsnag.start({
    apiKey,
    plugins: [new BugsnagPluginReact()],
    onError: (event) => {
      // Ignore Firestore offline/unavailable errors as they're expected behaviour
      const error = event.errors[0];
      if (error) {
        const errorMessage = error.errorMessage || '';
        const errorClass = error.errorClass || '';

        if (
          errorClass.includes('FirebaseError') ||
          errorClass.includes('FirestoreError') ||
          errorMessage.includes('unavailable') ||
          errorMessage.includes('Failed to get document because the client is offline') ||
          errorMessage.includes('Could not reach Cloud Firestore backend') ||
          errorMessage.includes("Backend didn't respond within 10 seconds")
        ) {
          const errorCode = (error as { code?: string }).code;
          if (errorCode === 'unavailable' || errorCode === 'deadline-exceeded') {
            return false;
          }
        }
      }
      return true;
    },
  });

  const plugin = Bugsnag.getPlugin('react');
  if (plugin) {
    ErrorBoundary = plugin.createErrorBoundary(React) as ComponentType<ErrorBoundaryProps>;
  }
} else if (import.meta.env.DEV) {
  console.warn(
    'Bugsnag API key is missing. Add VITE_BUGSNAG_API_KEY to enable error reporting.'
  );
}

export { ErrorBoundary };
